import { gql } from "@apollo/client";
import { createApolloClient } from "../../services/apolloClient";
import {
  AuthPayload,
  LoginInput,
  LoginResponse,
  RegisterInput,
  RegisterResponse,
  VerifyOtpInput,
  VerifyOtpResponse,
  VerifyPhoneInput,
  VerifyPhoneResponse,
  VerifyStatusResponse,
} from "./types";
import {
  CUSTOMER_REGISTER_MUTATION,
  REQUEST_SIGNUP_CODE_MUTATION,
  LOGIN_MUTATION,
  VERIFY_OTP_MUTATION,
  VERIFY_PHONE_MUTATION,
} from "./mutations";

const getErrorMessage = (error: unknown, fallback: string) => {
  if (!error || typeof error !== "object") {
    return fallback;
  }

  const graphQLErrors = (
    error as { graphQLErrors?: Array<{ message?: string }> }
  ).graphQLErrors;

  if (Array.isArray(graphQLErrors) && graphQLErrors.length > 0) {
    const firstMessage = graphQLErrors[0]?.message;
    if (firstMessage) {
      return firstMessage.replace(/^Error:\s*/i, "");
    }
  }

  if (error instanceof Error && error.message) {
    return error.message.replace(/^Error:\s*/i, "");
  }

  return fallback;
};

const client = createApolloClient();

// Resets only customer accounts (the same email may also have another kind of account)
const RESET_ACCOUNT = "customer";

const REQUEST_PASSWORD_RESET_MUTATION = gql`
  mutation RequestPasswordReset($email: String!, $account: PasswordResetAccount) {
    requestPasswordReset(email: $email, account: $account) {
      status
      message
    }
  }
`;

const RESET_PASSWORD_MUTATION = gql`
  mutation ResetPassword($email: String!, $code: String!, $newPassword: String!, $account: PasswordResetAccount) {
    resetPassword(email: $email, code: $code, new_password: $newPassword, account: $account) {
      status
      message
    }
  }
`;

export const authService = {
  /** Emails a 6-digit reset code (same reply whether or not the account exists) */
  async requestPasswordReset(email: string): Promise<string> {
    try {
      const result = await client.mutate<{ requestPasswordReset: { message: string } }>({
        mutation: REQUEST_PASSWORD_RESET_MUTATION,
        variables: { email, account: RESET_ACCOUNT },
      });
      return result.data?.requestPasswordReset.message ?? "";
    } catch (error) {
      throw new Error(getErrorMessage(error, "Couldn't send the code. Please try again"));
    }
  },

  async resetPassword(email: string, code: string, newPassword: string): Promise<string> {
    try {
      const result = await client.mutate<{ resetPassword: { message: string } }>({
        mutation: RESET_PASSWORD_MUTATION,
        variables: { email, code, newPassword, account: RESET_ACCOUNT },
      });
      return result.data?.resetPassword.message ?? "";
    } catch (error) {
      throw new Error(getErrorMessage(error, "Couldn't reset your password. Please try again"));
    }
  },

  /** Sign-up step 1: emails a 6-digit code to the address being registered */
  async requestSignupCode(email: string, firstName?: string): Promise<string> {
    try {
      const result = await client.mutate<{ requestSignupCode: { message: string } }>({
        mutation: REQUEST_SIGNUP_CODE_MUTATION,
        variables: { email, firstName },
      });
      return result.data?.requestSignupCode.message ?? "";
    } catch (error) {
      throw new Error(getErrorMessage(error, "Couldn't send the code. Please try again"));
    }
  },

  async customerRegister(input: RegisterInput): Promise<AuthPayload> {
    try {
      const result = await client.mutate({
        mutation: CUSTOMER_REGISTER_MUTATION,
        variables: input,
      });

      const data = result.data as RegisterResponse | undefined;

      if (!data?.customerRegister) {
        throw new Error("Register failed");
      }

      return data.customerRegister;
    } catch (error) {
      throw new Error(getErrorMessage(error, "Register failed"));
    }
  },

  async login(input: LoginInput): Promise<AuthPayload> {
    try {
      const result = await client.mutate({
        mutation: LOGIN_MUTATION,
        variables: input,
      });

      const data = result.data as LoginResponse | undefined;

      if (!data?.login) {
        throw new Error("Login failed");
      }

      return data.login;
    } catch (error) {
      throw new Error(getErrorMessage(error, "Login failed"));
    }
  },

  async verifyPhone(input: VerifyPhoneInput): Promise<VerifyStatusResponse> {
    try {
      const result = await client.mutate({
        mutation: VERIFY_PHONE_MUTATION,
        variables: input,
      });

      const data = result.data as VerifyPhoneResponse | undefined;

      if (!data?.verifyPhone) {
        throw new Error("Verify phone failed");
      }

      return data.verifyPhone;
    } catch (error) {
      throw new Error(getErrorMessage(error, "Verify phone failed"));
    }
  },

  async verifyOtp(input: VerifyOtpInput) {
    try {
      const result = await client.mutate({
        mutation: VERIFY_OTP_MUTATION,
        variables: input,
      });

      const data = result.data as VerifyOtpResponse | undefined;

      if (!data?.verifyOtp) {
        throw new Error("Verify OTP failed");
      }

      return data.verifyOtp;
    } catch (error) {
      throw new Error(getErrorMessage(error, "Verify OTP failed"));
    }
  },
};
